import React, { useState, useEffect } from "react";
import axios from "axios";
import FormularzZlecenia from "./zlecenia/FormularzZlecenia";
import FormularzEdycjiZlecenia from "./zlecenia/FormularzEdycjiZlecenia";
import "./componenetStyle/Zlecenia.css";
import { hasAnyPermission, hasOrderStatusPermission, hasPermission } from "../auth/permissionUtils";

const API_URL = process.env.REACT_APP_API_URL;

function getStatusClass(status) {
  switch (status) {
    case "Anulowane":
      return "status-anulowane";
    case "Rozliczone":
      return "status-rozliczone";
    case "Zrealizowane":
      return "status-zrealizowane";
    case "W realizacji":
      return "status-w-realizacji";
    case "Nowe":
      return "status-nowe";
    default:
      return "";
  }
}

function formatDate(dateString) {
  if (!dateString) return "Brak daty";
  const date = new Date(dateString);
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${hours}:${minutes} ${day}-${month}-${year}`;
}

function formatAddress(adres) {
  const nrDomu = adres.nr_domu != null ? String(adres.nr_domu).trim() : "";
  const nrLokalu = adres.nr_lokalu != null ? String(adres.nr_lokalu).trim() : "";
  const numerBudynku = nrDomu && nrLokalu ? `${nrDomu}/${nrLokalu}` : (nrDomu || nrLokalu);

  return [adres.miasto, adres.kod_pocztowy, adres.ulica, numerBudynku]
    .filter((part) => part !== null && part !== undefined && String(part).trim() !== "")
    .join(" ");
}

const Zlecenia = ({ uzytkownik }) => {
  const canCreateOrder = hasPermission(uzytkownik, "orders_create");
  const canEditOrderBase = hasAnyPermission(uzytkownik, ["orders_edit_all", "orders_edit_own"]);
  const canDeleteOrder = hasPermission(uzytkownik, "orders_delete_all");
  const [zlecenia, setZlecenia] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [kontynuacjaData, setKontynuacjaData] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editZlecenieId, setEditZlecenieId] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("ASC");

  const [towary, setTowary] = useState(null);
  const [slowniki, setSlowniki] = useState({
    kategorie: null,
    jednostki: null,
    stawkiVat: null,
  });

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        const [res, kat, jm, vat] = await Promise.all([
          fetch(`${API_URL}/towary-uslugi`, { signal: ac.signal, headers }),
          fetch(`${API_URL}/slowniki/kategorie`, { signal: ac.signal, headers }),
          fetch(`${API_URL}/slowniki/jednostki`, { signal: ac.signal, headers }),
          fetch(`${API_URL}/slowniki/stawki-vat`, { signal: ac.signal, headers }),
        ]);

        if (!res.ok || !kat.ok || !jm.ok || !vat.ok) throw new Error("Błąd pobierania danych");

        const [datares, datakat, datajm, datavat] = await Promise.all([
          res.json(),
          kat.json(),
          jm.json(),
          vat.json(),
        ]);

        setTowary(datares);
        setSlowniki({ kategorie: datakat, jednostki: datajm, stawkiVat: datavat });
      } catch (error) {
        if (error.name !== "AbortError") console.error(error);
      }
    })();
    return () => ac.abort();
  }, []);

  useEffect(() => {
    refreshZlecenia();
  }, [page, limit, searchTerm, sortField, sortOrder]);

  const refreshZlecenia = async () => {
    const token = localStorage.getItem("token");
    try {
      const resp = await axios.get(`${API_URL}/zlecenia`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page,
          limit,
          search: searchTerm || undefined,
          sortField: sortField || undefined,
          sortOrder,
        },
      });
      const payload = resp.data;
      const lista = Array.isArray(payload) ? payload : payload.zlecenia;
      const pages = Array.isArray(payload) ? 1 : payload.totalPages;

      setZlecenia(lista);
      setTotalPages(pages);
    } catch (err) {
      console.error("Błąd pobierania zleceń:", err);
    }
  };

  const handleSearch = () => {
    setPage(1);
    setSearchTerm(searchInput.trim());
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortField(field);
      setSortOrder("ASC");
    }
    setPage(1);
  };

  const handleEditClick = (id) => {
    setEditZlecenieId(id);
    setShowEditForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Czy na pewno chcesz usunąć to zlecenie?")) {
      const token = localStorage.getItem("token");
      try {
        await axios.delete(`${API_URL}/zlecenia/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        alert("Zlecenie zostało usunięte.");
        refreshZlecenia();
      } catch (err) {
        console.error("Błąd usuwania zlecenia:", err);
        alert("Nie udało się usunąć zlecenia.");
      }
    }
  };

  const renderSortIcon = (field) => {
    if (sortField === field) return sortOrder === "ASC" ? "🔼" : "🔽";
    return "⬍";
  };

  const displayedZlecenia = zlecenia.filter((zlecenie) =>
    hasOrderStatusPermission(uzytkownik, 'view', zlecenie.status)
  );

  return (
    <div className="zlecenia-container">
      <h2>Lista zleceń</h2>
      
      <div className="top-actions">
        {canCreateOrder
        ?<button className="add-button" onClick={() => setShowForm(true)}>
          Dodaj zlecenie
        </button>
        :<></>
        }
        

        <div className="search-box">
          <input
            type="text"
            placeholder="Szukaj zleceń..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <button onClick={handleSearch}>Szukaj</button>
        </div>
      </div>

      <div className="pagination-controls">
        <div className="limit-controls">
          
          {[20, 50, 100].map((n) => (
            <button
              key={n}
              onClick={() => { setLimit(n); setPage(1); }}
              className={limit === n ? "selected" : ""}
            >
              {n}
            </button>
          ))}
        </div>

        <div className="pagination-arrows">
          <button onClick={() => page > 1 && setPage(page - 1)} disabled={page === 1}>‹</button>
          <span> {page} / {totalPages} </span>
          <button onClick={() => page < totalPages && setPage(page + 1)} disabled={page === totalPages}>›</button>
        </div>
      </div>

      {/* MODALE */}
      {showForm && (
        <FormularzZlecenia
          uzytkownik={uzytkownik}
          onZamknij={() => { setShowForm(false); refreshZlecenia(); setKontynuacjaData(null); }}
          clientId={kontynuacjaData?.id_klient}
          defaultAddress={kontynuacjaData?.id_adres}
          continuation={kontynuacjaData}
          towary={towary}
          slowniki={slowniki}
        />
      )}

      {showEditForm && editZlecenieId && (
        <FormularzEdycjiZlecenia
          towary={towary}
          slowniki={slowniki}
          uzytkownik={uzytkownik}
          zlecenieId={editZlecenieId}
          onZamknij={() => { setShowEditForm(false); setEditZlecenieId(null); refreshZlecenia(); }}
          onKontynuacja={(data) => { setShowEditForm(false); setKontynuacjaData(data); setShowForm(true); }}
        />
      )}

      {/* WIDOK TABELI (Desktop) */}
      <div className="table-wrapper">
        <table className="zlecenia-table">
          <thead>
            <tr>
              <th onClick={() => handleSort("numer_zlecenia")}>Numer {renderSortIcon("numer_zlecenia")}</th>
              <th onClick={() => handleSort("rodzaj_zlecenia")}>Rodzaj {renderSortIcon("rodzaj_zlecenia")}</th>
              <th onClick={() => handleSort("status")}>Status {renderSortIcon("status")}</th>
              <th onClick={() => handleSort("klient")}>Klient {renderSortIcon("klient")}</th>
              <th onClick={() => handleSort("adres")}>Adres {renderSortIcon("adres")}</th>
              <th onClick={() => handleSort("pracownicy")}>Pracownicy {renderSortIcon("pracownicy")}</th>
              <th onClick={() => handleSort("opiekun")}>Opiekun {renderSortIcon("opiekun")}</th>
              <th onClick={() => handleSort("planowana_data_realizacji")}>Data {renderSortIcon("planowana_data_realizacji")}</th>
              <th>Akcje</th>
            </tr>
          </thead>
          <tbody>
            {displayedZlecenia.map((zlec) => (
              <tr key={zlec.id_zlecenia} className={getStatusClass(zlec.status)}>
                <td>{zlec.numer_zlecenia}</td>
                <td>{zlec.rodzaj_zlecenia}</td>
                <td>{zlec.status}</td>
                <td>{zlec.klient_imie} {zlec.klient_nazwisko}</td>
                <td>{formatAddress(zlec)}</td>
                <td className="pracownicy-lista-zlecen">
                  {zlec.pracownicy.length > 0
                    ? zlec.pracownicy.map((p) => `${p.imie} ${p.nazwisko}`).join(", ")
                    : "Nie przypisano"}
                </td>
                <td>{zlec.opiekun_imie} {zlec.opiekun_nazwisko}</td>
                <td>{formatDate(zlec.planowana_data_realizacji)}</td>
                <td>
                  <div className="action-buttons">
                    {canEditOrderBase && hasOrderStatusPermission(uzytkownik, 'edit', zlec.status)
                    ?<button onClick={() => handleEditClick(zlec.id_zlecenia)}>Edytuj</button>
                    :<></>
                    }
                    {canDeleteOrder && hasOrderStatusPermission(uzytkownik, 'delete', zlec.status)
                    ?<button onClick={() => handleDelete(zlec.id_zlecenia)}>Usuń</button>
                    :<></>
                    }
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* WIDOK KART (Mobile) */}
      <div className="zlecenia-cards">
        {displayedZlecenia.map((zlec) => (
          <div key={zlec.id_zlecenia} className={`zlecenie-card ${getStatusClass(zlec.status)}`}>
            <div className="card-header">
              <div className="card-header-main">
                <strong className="card-order-number">{zlec.numer_zlecenia}</strong>
                <span className="card-order-type">{zlec.rodzaj_zlecenia}</span>
              </div>
              <span className="card-status">{zlec.status}</span>
            </div>
            <div className="card-body">
              <div className="card-info-stack">
                <p className="card-line card-client-name">{zlec.klient_imie} {zlec.klient_nazwisko}</p>
                <p className="card-line">{formatAddress(zlec)}</p>
                <p className="card-line card-contact-line">
                  {zlec.nr_telefonu || "Brak numeru telefonu"}
                  <span className="card-separator">•</span>
                  {zlec.adres_email || "Brak adresu e-mail"}
                </p>
                <p className="card-line card-date-line">{formatDate(zlec.planowana_data_realizacji)}</p>
              </div>
            </div>
            <div className="card-actions">
              {canEditOrderBase && hasOrderStatusPermission(uzytkownik, 'edit', zlec.status)
                ?<button onClick={() => handleEditClick(zlec.id_zlecenia)}>Edytuj</button>
                :<></>
              }
              {canDeleteOrder && hasOrderStatusPermission(uzytkownik, 'delete', zlec.status)
                ?<button onClick={() => handleDelete(zlec.id_zlecenia)} >Usuń</button>
                :<></>
              }
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Zlecenia;